import React, { useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import './ToggleSwitch.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';  // To generate unique request IDs
import { horizontalApi, serviceApi, ManagerlApi, baseApi } from '../../API/api';  // Make sure to import your API URL

const HorizontalCurvesTab = () => {
  const [step, setStep] = useState(1);
  const [isReport, setIsReport] = useState(false);
  const [dbName, setDbName] = useState('');
  const [collectionName, setCollectionName] = useState('');
  const [outputFolder, setOutputFolder] = useState('');
  const [rows, setRows] = useState([{ Start: '', End: '', Speed: '' }]);
  const [additionalData, setAdditionalData] = useState({
    project_name: '',
    state: '',
    road_number: '',
    start_mile: '',
    end_mile: '',
    job_number: '',
    corridor: ''
  });
  const [submittedData, setSubmittedData] = useState(null);
  const [showRunButton, setShowRunButton] = useState(false);
  const [requests, setRequests] = useState([]); // To track request statuses
  const [formErrors, setFormErrors] = useState(''); // Error message state

  const getFormattedTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const handleAddRow = () => {
    setRows([...rows, { Start: '', End: '', Speed: '' }]);
  };

  const handleRemoveRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
  };

  const handleChange = (index, field, value) => {
    const newRows = [...rows];
  
    if (['Start', 'End', 'Speed'].includes(field)) {
      newRows[index][field] = isNaN(value) ? '' : value;
    } else {
      newRows[index][field] = value;
    }
  
    setRows(newRows);
  };

  const handleAdditionalDataChange = (field, value) => {
    setAdditionalData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const extractFolderId = (url) => {
    const parts = url.split('/');
    setOutputFolder(parts[parts.length - 1]);
  };

  const handleToggle = () => {
    setIsReport(!isReport);
    if (!isReport) {
      resetForm();
    }
    handleClean()
  };

  const resetForm = () => {
    setStep(1);
    setAdditionalData({
      project_name: '',
      state: '',
      road_number: '',
      start_mile: '',
      end_mile: '',
      job_number: '',
      corridor: ''
    });
    setOutputFolder('');  // Clear output folder
    setSubmittedData(null);
    setShowRunButton(false);  // Hide Run button
    setFormErrors(''); // Clear errors
  };

  const validateForm = () => {
    const { project_name, state, road_number, start_mile, end_mile, job_number, corridor } = additionalData;
    const errors = [];

    if (!project_name) errors.push('Project Name');
    if (!state) errors.push('State');
    if (!road_number) errors.push('Road Number');
    if (!start_mile) errors.push('Start Mile');
    if (!end_mile) errors.push('End Mile');
    if (!job_number) errors.push('Job Number');
    if (!corridor) errors.push('Corridor');
    if (!outputFolder) errors.push('Report Output Folder');

    if (errors.length > 0) {
      setFormErrors('Please fill out all the fields');
      return false;
    } else {
      setFormErrors('');
      return true;
    }
  };

  const handleDone = () => {
    if (validateForm()) {
      const payload = {
        ...additionalData,
        database_name: dbName,
        collection_name: collectionName,
        output_drive_folder_id: outputFolder,
        report: isReport,
        speed_table: rows.map((row) => ({
          Start: isNaN(parseInt(row.Start, 10)) ? '' : parseInt(row.Start, 10),
          End: isNaN(parseInt(row.End, 10)) ? '' : parseInt(row.End, 10),
          Speed: isNaN(parseInt(row.Speed, 10)) ? '' : parseInt(row.Speed, 10),
        })),
      };
      setSubmittedData(payload);
      setStep(1);  // Reset wizard step
      setShowRunButton(true);  // Show Run button
    }
  };

  const handleRunHorizontalCurve = async () => {
    const apiUrl = ManagerlApi + '/horizontal';
    const requestId = uuidv4();
    const startTime = getFormattedTime();

    // Conditionally construct the payload based on whether a report is requested
    const payload = isReport
      ? {
          ...additionalData,
          database_name: dbName,
          collection_name: collectionName,
          output_drive_folder_id: outputFolder,
          report: isReport,
          speed_table: rows.map((row) => ({
            Start: isNaN(parseInt(row.Start, 10)) ? '' : parseInt(row.Start, 10),
            End: isNaN(parseInt(row.End, 10)) ? '' : parseInt(row.End, 10),
            Speed: isNaN(parseInt(row.Speed, 10)) ? '' : parseInt(row.Speed, 10),
          })),
        }
      : {
          database_name: dbName,
          collection_name: collectionName,
          speed_table: rows.map((row) => ({
            Start: isNaN(parseInt(row.Start, 10)) ? '' : parseInt(row.Start, 10),
            End: isNaN(parseInt(row.End, 10)) ? '' : parseInt(row.End, 10),
            Speed: isNaN(parseInt(row.Speed, 10)) ? '' : parseInt(row.Speed, 10),
          })),
        };

    // Add the request to the list with start time and default status
    setRequests((prevRequests) => [
      ...prevRequests,
      { id: requestId, collectionName, status: 'Sent', startTime },
    ]);

    try {
      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Update the request status in the list with response message
      const statusMessage = response.data.message || 'Task Completed';
      setRequests((prevRequests) =>
        prevRequests.map((request) =>
          request.id === requestId
            ? { ...request, status: `${statusMessage} ✅` }
            : request
        )
      );
    } catch (error) {
      console.error('There was an error running the horizontal curves script!', error);

      // Update the request status in the list with error message
      const errorMessage = error.response?.data?.message || 'Task Failed';
      setRequests((prevRequests) =>
        prevRequests.map((request) =>
          request.id === requestId
            ? { ...request, status: `${errorMessage} ❌` }
            : request
        )
      );
    }
  };

  const handleDeleteRequest = (id) => {
    setRequests((prevRequests) =>
      prevRequests.filter((request) => request.id !== id)
    );
  };

  const handleClean = () => {
    resetForm();  // Reset all the form data
  };

  const handleClearAll = () => {
    setRequests([]);  // Clear all request statuses
  };

  const handleNextStep = () => {
    setStep((prevStep) => prevStep + 1);
  };

  const handlePrevStep = () => {
    setStep((prevStep) => prevStep - 1);
  };

  return (
    <div className='HorizontalContent'>
      <div className='start'>
        {/* Database Name Input */}
        <div>
        <h5>Enter Horizontal Curve Details:</h5>

          <label>Database Name:</label>
          <input
            type="text"
            value={dbName}
            onChange={(e) => setDbName(e.target.value)}
          />
        </div>

        {/* Collection Name Input */}
        <div>
          <label>Collection Name:</label>
          <input
            type="text"
            value={collectionName}
            onChange={(e) => setCollectionName(e.target.value)}
          />
        </div>

        {/* Speed Table Inputs */}
        <div className="Speed-table">
          <div>
            <label>Speed Table:</label>
          </div>
          {rows.map((row, index) => (
            <div 
            key={index}>
              <input
                type="text"
                placeholder="Start"
                value={row.Start}
                onChange={(e) => handleChange(index, 'Start', e.target.value)}
              />
              <input
                type="text"
                placeholder="End"
                value={row.End}
                onChange={(e) => handleChange(index, 'End', e.target.value)}
              />
              <input
                type="text"
                placeholder="Speed"
                value={row.Speed}
                onChange={(e) => handleChange(index, 'Speed', e.target.value)}
              />
              {rows.length > 1 && (
                <button onClick={() => handleRemoveRow(index)} className="remove-row-btn">
                  <FaTrash style={{ color: 'red' }} />
                </button>
              )}
            </div>
          ))}
          <button onClick={handleAddRow}>Add row</button>
        </div>

        {/* Toggle Switch for Report */}
        <div>
          <label>
            <input
              type="checkbox"
              checked={isReport}
              onChange={handleToggle}
            />
          </label>
          <span>Create Report</span>
        </div>

        {/* If no report, show the Run button here */}
        {!isReport && (
          <div>
            <button onClick={handleRunHorizontalCurve} className={`run-btn greenButton`}>
              Run Horizontal Curve
            </button>
          </div>
        )}
            {/* Request Status Table */}
      <div className='request-list'>
        <br />
        <h5>Request Status:</h5>
        <table>
          <thead>
            <tr>
              <th>Request ID</th>
              <th>Status</th>
              <th>Start Time</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request.id}>
                <td>{request.collectionName}</td>
                <td>{request.status}</td>
                <td>{request.startTime}</td>
                <td>
                  <button onClick={() => handleDeleteRequest(request.id)}>
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className='clear-all-button' onClick={handleClearAll}>
          Remove All
        </button>
      </div>
      </div>

      {/* Wizard for Report Data */}
      {isReport && (
        <div className="wizard-container">
         <div className="form-errors">
           {formErrors && <p style={{ color: 'red' }}>{formErrors}</p>}
         </div>
         <h5>Enter Report Details</h5>
         <label>Project Name</label>
         <input
           type="text"
           value={additionalData.project_name}
           onChange={(e) => handleAdditionalDataChange('project_name', e.target.value)}
         />
         <label>State</label>
         <input
           type="text"
           value={additionalData.state}
           onChange={(e) => handleAdditionalDataChange('state', e.target.value)}
         />
         <label>Road Number</label>
         <input
           type="text"
           value={additionalData.road_number}
           onChange={(e) => handleAdditionalDataChange('road_number', e.target.value)}
         />
         <label>Start Mile</label>
         <input
           type="text"
           value={additionalData.start_mile}
           onChange={(e) => handleAdditionalDataChange('start_mile', e.target.value)}
         />
         <label>End Mile</label>
         <input
           type="text"
           value={additionalData.end_mile}
           onChange={(e) => handleAdditionalDataChange('end_mile', e.target.value)}
         />
         <label>Job Number</label>
         <input
           type="text"
           value={additionalData.job_number}
           onChange={(e) => handleAdditionalDataChange('job_number', e.target.value)}
         />
         <label>Corridor</label>
         <input
           type="text"
           value={additionalData.corridor}
           onChange={(e) => handleAdditionalDataChange('corridor', e.target.value)}
         />
         <label>Report Output Folder</label>
         <input
           type="text"
           placeholder="Google Drive Folder URL"
           value={outputFolder}
           onChange={(e) => extractFolderId(e.target.value)}
         />

         <div className="button-container">
           <button onClick={handleDone}>Submit</button>
         </div>
       </div>
      )}

      {/* Summary of Submitted Data */}
      {submittedData && (
        <div className="summary">
          <h5>Summary of Report Data:</h5>
          <ul className="summary-list">
            <li className="even-row"><strong>Project Name:</strong> {submittedData.project_name}</li>
            <li className="odd-row"><strong>State:</strong> {submittedData.state}</li>
            <li className="even-row"><strong>Road Number:</strong> {submittedData.road_number}</li>
            <li className="odd-row"><strong>Start Mile:</strong> {submittedData.start_mile}</li>
            <li className="even-row"><strong>End Mile:</strong> {submittedData.end_mile}</li>
            <li className="odd-row"><strong>Job Number:</strong> {submittedData.job_number}</li>
            <li className="even-row"><strong>Corridor:</strong> {submittedData.corridor}</li>
            <li className="odd-row"><strong>Database Name:</strong> {submittedData.database_name}</li>
            <li className="even-row"><strong>Collection Name:</strong> {submittedData.collection_name}</li>
            <li className="odd-row"><strong>Report Output Folder:</strong> {submittedData.output_drive_folder_id}</li>
            <li className="even-row"><strong>Speed Table: </strong>
               {submittedData.speed_table.map((row, index) => (
              <li key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                Start: {row.Start !== null && row.Start !== undefined && !isNaN(row.Start) ? row.Start : 'N/A'}, 
                End: {row.End !== null && row.End !== undefined && !isNaN(row.End) ? row.End : 'N/A'}, 
                Speed: {row.Speed !== null && row.Speed !== undefined && !isNaN(row.Speed) ? row.Speed : 'N/A'}
              </li>
              ))}
             </li>
          </ul>

          {/* Run Horizontal Curve and Clean Buttons */}
          {showRunButton && (
            <div>
              <button className='greenButton' onClick={handleRunHorizontalCurve}>Run Horizontal Curve</button>
              <button onClick={handleClean} style={{ marginLeft: '10px' }}>Clean</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HorizontalCurvesTab;
